<template>
    <div id="conversation" class="conversation-wrapper">
        <ConversationSkeleton v-if="loadingState === 'initializing'" />

        <template v-else-if="loadingState === 'ready'">
            <div class="conversation-container js-scroller">
                <div class="conversation-scroller">
                    <ConversationMessages
                        :conversations-client="conversationsClient"
                        :conversation="selectedConversation"
                        :messages="messages"
                        @reply-to="setReplyTo" />
                    <TypingBubble v-if="isTyping" />
                    <LoadingIndicator v-if="showLoadingIndicator" />
                </div>
            </div>

            <div>
                <div class="reply-to-message" v-if="replyToMessage">
                    <strong>{{ handleTwilioUserNameOf(replyToMessageUser) }}</strong
                    ><br />
                    <p>{{ replyToMessage.body }}</p>
                    <div v-if="replyToMessageHasTwilioMedia" class="message-media">
                        <div class="icon-media"><img src="/img/icons/media.svg" alt="media" /> Photo</div>
                        <img class="media-reply" :src="replyToMessageMediaUrl" />
                    </div>
                    <div v-else-if="replyToMessageHasLocalMedia" class="message-media">
                        <template v-if="replyToMessage.attributes.media.is_image">
                            <div class="icon-media d-flex align-items-center"><img src="/img/icons/media.svg" alt="media" /> Photo</div>
                            <img class="media-reply" :src="replyToMessageMediaUrl" />
                        </template>
                        <template v-else-if="replyToMessage.attributes.media.is_video">
                            <div class="icon-media d-flex align-items-center"><img src="/img/icons/video.svg" alt="media" /> Video</div>
                            <img class="media-reply" :src="replyToMessageMediaUrl" />
                        </template>
                        <template v-else-if="replyToMessage.attributes.media.is_audio">
                            <div class="icon-media d-flex align-items-center audio"><img src="/img/icons/audio.svg" alt="media" /> Audio</div>
                            <div class="is-audio">
                                <audio controls :src="replyToMessageMediaUrl" class="audio-reply-file">
                                    <a :href="replyToMessageMediaUrl" target="_blank"> Download audio </a>
                                </audio>
                            </div>
                        </template>
                        <template v-else>
                            {{ replyToMessage.attributes.media.file_name }}
                        </template>
                    </div>
                    <span class="close-reply" @click.prevent="replyToMessage = null"><img src="/img/icons/close-reply.svg" alt="close" /></span>
                </div>
                <div class="conversation-footer" v-if="canContinueConversation">
                    <vue-dropzone ref="vueDropzone" id="dropzone" :options="dropzoneOptions" @vdropzone-file-added="onAddMedia"></vue-dropzone>
                    <form @submit.prevent="sendMessage">
                        <textarea
                            name="message"
                            id="message"
                            class="form-control"
                            style="resize: none; background: white; min-height: 44px; border: 1px solid #f4e7f0; padding-top: 11px"
                            rows="1"
                            max-rows="4"
                            placeholder="Type your message"
                            autoComplete="off"
                            :disabled="loadingState !== 'ready'"
                            v-model="newMessage"
                            @focus="patchTextareaMaxRowsSupport($event.target)"></textarea>
                        <button type="submit" class="btn btn--primary">
                            <img src="/img/arrow-up-line.svg" alt="arrow up" class="conversation__arow-icon" />
                        </button>
                    </form>
                </div>
            </div>
        </template>
        <div v-else>
            <div class="p-5">
                <span v-text="loadingState !== 'failed' ? 'Loading messages...' : 'Conversation cannot be initialized.'"></span>
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment';
import ConversationSkeleton from '@components/UserChat/ConversationSkeleton';
import ConversationMessages from '@components/UserChat/ConversationMessages';
import TypingBubble from '@components/UserChat/TypingBubble';
import LoadingIndicator from '@components/UserChat/LoadingIndicator';
import vue2Dropzone from 'vue2-dropzone';
import { handleTwilioUserNameOf } from '@helpers/helpers';
// import 'vue2-dropzone/dist/vue2Dropzone.min.css';
import patchTextareaMaxRowsSupport from '@helpers/textarea-max-rows';

export default {
    components: {
        ConversationMessages,
        ConversationSkeleton,
        TypingBubble,
        LoadingIndicator,
        vueDropzone: vue2Dropzone,
    },
    props: {
        conversationsClient: {
            type: Object,
            required: true,
            default: null,
        },
        selectedConversation: {
            required: true,
        },
    },
    data() {
        return {
            newMessage: '',
            messages: [],
            conversationProxy: undefined,
            loadingState: 'initializing',
            isTyping: false,
            boundConversations: new Set(),
            dropzoneOptions: {
                url: 'https://httpbin.org/post',
                thumbnailWidth: 150,
                maxFilesize: 0.5,
                autoProcessQueue: false,
                addRemoveLinks: true,
                acceptedFiles: 'image/*,video/*,audio/*',
                dictDefaultMessage: '<img src="/img/icons/attachment-icon.svg" width="44" height="44"></i>',
            },
            canContinueConversation: false,
            replyToMessage: null,
            replyToMessageUser: null,
            replyToMessageHasTwilioMedia: false,
            replyToMessageHasLocalMedia: false,
            replyToMessageMediaUrl: null,
            showLoadingIndicator: false,
        };
    },
    async mounted() {
        this.conversationProxy = this.selectedConversation;
        this.setCanContinueConversation(this.conversationProxy);
    },
    watch: {
        selectedConversation(selectedConversation) {
            this.conversationProxy = selectedConversation;
            this.replyToMessage = null;
            this.replyToMessageUser = null;
            this.replyToMessageHasTwilioMedia = false;
            this.replyToMessageHasLocalMedia = false;
            this.replyToMessageMediaUrl = null;
            this.isTyping = false;
        },
        conversationProxy(conversationProxy, oldConversationProxy) {
            const notTheSameConversation = conversationProxy !== oldConversationProxy;
            if (notTheSameConversation) {
                this.loadMessagesFor(conversationProxy);
                if (!this.boundConversations.has(conversationProxy)) {
                    const newConversation = conversationProxy;
                    newConversation.on('messageAdded', (message) => {
                        this.messageAdded(message, newConversation);
                        this.setCanContinueConversation(newConversation);
                    });
                    newConversation.on('typingStarted', (participant) => {
                        this.setTyping(true, participant);
                    });
                    newConversation.on('typingEnded', (participant) => {
                        this.setTyping(false, participant);
                    });
                    this.boundConversations = new Set([...this.boundConversations, newConversation]);
                }
            }
            if (this.loadingState === 'initializing' || notTheSameConversation) {
                this.loadingState = 'loading messages';
                this.conversationProxy = conversationProxy;
            }
            if (this.loadingState === 'ready' || notTheSameConversation) {
                this.$router.push({ params: { conversationSid: conversationProxy.sid } });
            }
        },
    },
    methods: {
        handleTwilioUserNameOf,
        patchTextareaMaxRowsSupport,
        loadMessagesFor(thisConversation) {
            if (this.conversationProxy === thisConversation) {
                thisConversation
                    .getMessages(parseInt(process.env.MIX_TWILIO_CONVERSATIONS_GET_MESSAGES_NUMBER, 10))
                    .then((messagePaginator) => {
                        if (this.conversationProxy === thisConversation) {
                            this.messages = this.mapMessages(messagePaginator.items);
                            this.loadingState = 'ready';
                        }
                    })
                    .catch((err) => {
                        console.error("Couldn't fetch messages IMPLEMENT RETRY", err);
                        this.loadingState = 'failed';
                    });
            }
        },
        mapMessages(messages) {
            const messagesMainDates = [];
            return messages.map((message) => {
                const date = moment(message.state.timestamp);
                const formattedDate = date.format('DD/MM/YYYY');
                let mainDate = formattedDate;
                if (!messagesMainDates.includes(formattedDate)) {
                    messagesMainDates.push(formattedDate);
                    const yesterdayFormatted = moment().subtract(1, 'days').format('DD/MM/YYYY');
                    const todayFormatted = moment().format('DD/MM/YYYY');
                    if (formattedDate === yesterdayFormatted) {
                        mainDate = 'Yesterday';
                    } else if (formattedDate === todayFormatted) {
                        mainDate = 'Today';
                    } else {
                        mainDate = date.format('ddd, DD MMM');
                    }
                } else {
                    mainDate = null;
                }

                return Object.assign(message, { mainDate });
            });
        },
        messageAdded(message, targetConversation) {
            if (targetConversation === this.conversationProxy) {
                this.messages = [...this.messages, message];
            }
        },
        sendMessage(event) {
            if (event) {
                event.preventDefault();
            }

            const message = this.newMessage;

            if (message) {
                this.newMessage = '';
                const messageAttributes = {};
                if (this.replyToMessage) {
                    messageAttributes.replyToIndex = this.replyToMessage.index;
                    this.replyToMessage = null;
                    this.replyToMessageUser = null;
                }
                this.conversationProxy.sendMessage(message, messageAttributes);
            }
        },
        async setReplyTo(message) {
            this.replyToMessageUser = await this.conversationsClient.getUser(message.author);
            this.replyToMessage = message;
            this.replyToMessageHasTwilioMedia = this.replyToMessage.type === 'media';
            this.replyToMessageHasLocalMedia = this.replyToMessage.attributes.hasOwnProperty('media');

            if (this.replyToMessageHasTwilioMedia) {
                this.replyToMessage.media.getContentTemporaryUrl().then((url) => {
                    this.replyToMessageMediaUrl = url;
                });
            }
            if (this.replyToMessageHasLocalMedia) {
                if (this.replyToMessage.attributes.media.is_image) {
                    this.replyToMessageMediaUrl = this.replyToMessage.attributes.media.urls.thumb;
                }
                if (this.replyToMessage.attributes.media.is_video) {
                    this.replyToMessageMediaUrl = this.replyToMessage.attributes.media.urls.thumb;
                }
                if (this.replyToMessage.attributes.media.is_audio) {
                    this.replyToMessageMediaUrl = this.replyToMessage.attributes.media.source[0].src;
                }
                if (this.replyToMessage.attributes.media.is_other) {
                    this.replyToMessageMediaUrl = this.replyToMessage.attributes.media.urls.src;
                }
            }
        },
        async setCanContinueConversation(conversation) {
            if (conversation !== this.conversationProxy) {
                return;
            }
            const participants = await conversation.getParticipants();
            participants.forEach(async (participant) => {
                const user = await participant.getUser();
                this.canContinueConversation = this.canContinueConversation || (user.identity !== this.conversationsClient.user.identity && !user.attributes.is_deleted);
            });
        },
        onAddMedia(file) {
            const formData = new FormData();
            formData.append('media', file);
            const headers = {
                'Content-Type': 'multipart/form-data',
            };
            this.showLoadingIndicator = true;
            this.axios
                .post(`/api/chat/${this.conversationProxy.sid}/upload`, formData, { headers })
                .then(({ data }) => data)
                .then(({ data }) => {
                    this.conversationProxy.sendMessage('', { media: data });
                })
                .catch((error) => {
                    window.show422Errors(error);
                })
                .finally(() => {
                    this.showLoadingIndicator = false;
                });
            this.$refs.vueDropzone.removeFile(file);
        },
        // enables sending messages with enter; client requested it to be removed for now
        // onKeydown(event) {
        //     if (event.keyCode === 13) {
        //         event.preventDefault();
        //         this.sendMessage();
        //         return;
        //     }
        //     this.conversationProxy.typing();
        // },
        setTyping(isTyping, participant) {
            if (participant.conversation.sid === this.selectedConversation.sid) {
                this.isTyping = isTyping;
            } else {
                this.isTyping = false;
            }
        },
    },
};
</script>
